import React from "react";
import { Info, Wallet } from "@phosphor-icons/react";

import { Button } from "../../../design-system/components/desktop";
import { Cta } from "../../../design-system/components/mobile";
import { useViewport } from "../../../design-system/hooks/useViewport";
import {
  Panel,
  PanelNote,
} from "../../../design-system/patterns/settings";
import { formatCurrency } from "../../../utils/common";

/**
 * Wallet. Pure.
 *
 * The library has no wallet and no payout concept, so this is designed from the
 * two facts the API actually returns: a balance, and whether a bank account
 * exists to pay it into. No fee row, no arrival estimate, no destination bank
 * name, because none of those are in the payload.
 *
 * "Request payout" is the one place on /profile where an INK command belongs:
 * it is a commit, not a navigation. It stays disabled with a stated reason
 * whenever it cannot work, because a live button that fails is worse than a
 * flat one that explains itself. Disabled is the real attribute, so the flat
 * grey comes from the CSS and never from opacity.
 *
 * Amounts are Rands via `formatCurrency`, which prefixes `R`.
 */
const WalletSection = ({ wallet, hasBankDetails = false, onRequestPayout }) => {
  const { isDesktop } = useViewport();
  const balance = wallet?.balance ?? 0;
  const nothingToDraw = !balance || balance <= 0;
  const blocked = nothingToDraw || !hasBankDetails;

  const Commit = isDesktop ? Button : Cta;
  const commitProps = isDesktop
    ? { variant: "primary" }
    : { variant: "solid", block: true };

  return (
    <Panel icon={Wallet} title="Wallet">
      <div className="pfx-wallet">
        <div className="pfx-wallet__figure">
          <span className="pfx-wallet__label">Current balance</span>
          <span className="pfx-wallet__amount">{formatCurrency(balance)}</span>
        </div>
        <Commit {...commitProps} onClick={onRequestPayout} disabled={blocked}>
          Request payout
        </Commit>
      </div>

      {!hasBankDetails ? (
        <PanelNote icon={Info}>
          Add a payment method below before you can request a payout.
        </PanelNote>
      ) : nothingToDraw ? (
        <PanelNote icon={Info}>
          There is nothing to draw yet. Your balance grows as orders are finalised.
        </PanelNote>
      ) : null}
    </Panel>
  );
};

export default WalletSection;
